import { KatalComponent, register } from '../../shared/base';

/**
 * @component {kat-thumbnail} KatalThumbnail provides the ability to present a reduced size image in one of three sizes.
 * @status hidden
 * @example Small {"src": "https://www.gannett-cdn.com/-mm-/f6191258f02f99657caf0c9f1bbb8b19b826f345/c=0-314-3351-4782&r=537&c=0-0-534-712/local/-/media/2016/04/21/USATODAY/USATODAY/635968409312380085-PRINCE-KO-65900070.JPG", "size": "small"}
 * @example Medium {"src": "https://www.gannett-cdn.com/-mm-/f6191258f02f99657caf0c9f1bbb8b19b826f345/c=0-314-3351-4782&r=537&c=0-0-534-712/local/-/media/2016/04/21/USATODAY/USATODAY/635968409312380085-PRINCE-KO-65900070.JPG", "size": "medium"}
 * @example Large {"src": "https://www.gannett-cdn.com/-mm-/f6191258f02f99657caf0c9f1bbb8b19b826f345/c=0-314-3351-4782&r=537&c=0-0-534-712/local/-/media/2016/04/21/USATODAY/USATODAY/635968409312380085-PRINCE-KO-65900070.JPG", "size": "large"}
 * @guideline Do Always provide alternate text for screenreaders via the alt attribute.
 * @guideline Do Use Thumbnails for information-dense displays where a visual representation either enhances or takes the place of a textual representation.
 * @guideline Dont Use a full-size image as the src.
 * @guideline Dont Don't use a thumbnail to build a carousel or shoveler component. These are better served by higher-resolution images.
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-thumbnail')
export class KatThumbnail extends KatalComponent {
  static get observedAttributes() {
    return ['src', 'size', 'alt-text'];
  }

  constructor() {
    super(KatThumbnail.observedAttributes);
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  /**
   * @classprop {string} src This is the URL of the thumbnail image
   * @required
   */
  get src() {
    return this.getAttributeOrDefault('src', '');
  }

  set src(value) {
    this.setAttribute('src', value);
  }

  /**
   * @classprop {enum} size Specifies the size of the thumbnail. Defaults to small
   * @enum {value} small default size
   * @enum {value} medium
   * @enum {value} large
   * @required
   */
  get size() {
    if (!this.hasAttribute('size')) {
      this.setAttribute('size', 'small');
    }

    return this.getAttribute('size');
  }

  set size(value) {
    this.setAttribute('size', value);
  }

  /**
   * @classprop {string} alt-text Reflects to the HTML img tag's alt attribute.
   * @required
   */
  get altText() {
    return this.getAttribute('alt-text') || ' ';
  }

  set altText(value) {
    this.removeOrSetAttribute('alt-text', value);
  }

  render() {
    //Clear components innerHTML
    this.innerHTML = '';

    // Create img element
    const imgElement = document.createElement('img');

    // Set attributes
    imgElement.setAttribute('src', this.src);
    imgElement.setAttribute('alt', this.altText);

    // Append element to custom element
    this.appendChild(imgElement);
  }

  // Respond to attribute changes.
  attributeChangedCallback() {
    this.render();
  }
}
